import React, { useState, useEffect, useMemo } from 'react';
import {
    Search, Filter, Eye, Check, X, Video, UserPlus,
    Download, ArrowRight, Loader2, AlertCircle,
    MoreHorizontal, Clipboard, Shield, UserCheck, Clock,
    Mail, Phone, GraduationCap, Briefcase, FileText, Award
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.intuitlms.online/api';

const getDocUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;

    // Fallback logic if API_BASE_URL is relative or weird
    let apiRoot = 'https://api.intuitlms.online';

    const cleanPath = url.startsWith('/') ? url : `/${url}`;
    // Ensure we don't have double /uploads
    const finalPath = cleanPath.startsWith('/uploads') ? cleanPath : `/uploads${cleanPath}`;

    return `${apiRoot}${finalPath}`;
};

const UserManagement = () => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('applications');
    const [applications, setApplications] = useState([]);
    const [activeLecturers, setActiveLecturers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [selectedApp, setSelectedApp] = useState(null);
    const [showDetailPanel, setShowDetailPanel] = useState(false);
    const [showCredentialsModal, setShowCredentialsModal] = useState(false);
    const [credentials, setCredentials] = useState(null);
    const [notification, setNotification] = useState({ show: false, message: '', type: '' });
    const [isActionLoading, setIsActionLoading] = useState(false);

    useEffect(() => {
        fetchApplications();
        fetchActiveLecturers();
    }, []);

    const fetchApplications = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE_URL}/lecturers/applications/all`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setApplications(response.data);
        } catch (err) {
            console.error('Fetch apps error:', err);
            showNotification('Failed to fetch applications', 'error');
        } finally {
            setLoading(false);
        }
    };

    const fetchActiveLecturers = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_BASE_URL}/lecturers`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setActiveLecturers(response.data);
        } catch (err) {
            console.error('Fetch lecturers error:', err);
        }
    };

    const showNotification = (message, type = 'success') => {
        setNotification({ show: true, message, type });
        setTimeout(() => setNotification({ show: false, message: '', type: '' }), 5000);
    };

    const handleApprove = async (appId) => {
        if (!window.confirm('Are you sure you want to approve this application?')) return;

        setIsActionLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.put(`${API_BASE_URL}/lecturers/applications/${appId}/approve`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setCredentials({
                email: response.data.email || applications.find(a => a.id === appId).email,
                password: response.data.generatedPassword
            });
            setShowCredentialsModal(true);
            showNotification('Application approved successfully');
            fetchApplications();
            fetchActiveLecturers();
        } catch (err) {
            console.error(err);
            showNotification(err.response?.data?.message || 'Approval failed', 'error');
        } finally {
            setIsActionLoading(false);
        }
    };

    const handleReject = async (appId) => {
        const reason = window.prompt('Enter rejection reason:');
        if (reason === null) return;

        setIsActionLoading(true);
        try {
            const token = localStorage.getItem('token');
            await axios.put(`${API_BASE_URL}/lecturers/applications/${appId}/reject`, { rejectionReason: reason }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            showNotification('Application rejected');
            fetchApplications();
        } catch (err) {
            console.error(err);
            showNotification('Rejection failed', 'error');
        } finally {
            setIsActionLoading(false);
        }
    };

    const handleScheduleInterview = async (appId) => {
        setIsActionLoading(true);
        try {
            const token = localStorage.getItem('token');
            await axios.post(`${API_BASE_URL}/lecturers/applications/${appId}/schedule-interview`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            showNotification('Interview scheduled');
            fetchApplications();
        } catch (err) {
            console.error(err);
            showNotification('Scheduling failed', 'error');
        } finally {
            setIsActionLoading(false);
        }
    };

    const handleToggleInterviewActive = async (appId, currentState) => {
        setIsActionLoading(true);
        try {
            const token = localStorage.getItem('token');
            await axios.put(`${API_BASE_URL}/lecturers/applications/${appId}/start-interview`, { active: !currentState }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            showNotification(currentState ? 'Interview ended' : 'Interview activated');
            fetchApplications();
        } catch (err) {
            console.error(err);
            showNotification('Action failed', 'error');
        } finally {
            setIsActionLoading(false);
        }
    };

    const handleVerifyDocument = async (appId, documentKey, status) => {
        let reason = '';
        if (status === 'rejected') {
            reason = window.prompt('Enter rejection reason for this document:');
            if (reason === null) return;
        }

        setIsActionLoading(true);
        try {
            const token = localStorage.getItem('token');
            await axios.put(`${API_BASE_URL}/lecturers/applications/${appId}/verify-document`, {
                documentKey,
                status,
                reason
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            showNotification(`Document ${status}`);
            fetchApplications();
            // Update selectedApp locally to reflect change
            const updatedApp = await axios.get(`${API_BASE_URL}/lecturers/applications/${appId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSelectedApp(updatedApp.data);
        } catch (err) {
            console.error(err);
            showNotification('Failed to verify document', 'error');
        } finally {
            setIsActionLoading(false);
        }
    };

    const filteredApps = useMemo(() => {
        return applications.filter(app => {
            const matchesSearch = app.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                app.email.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
            return matchesSearch && matchesStatus;
        });
    }, [applications, searchTerm, statusFilter]);

    const stats = {
        total: applications.length,
        pending: applications.filter(a => a.status === 'pending').length,
        scheduled: applications.filter(a => a.status === 'interview_scheduled').length,
        approved: applications.filter(a => a.status === 'approved').length
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        showNotification('Password copied to clipboard');
    };

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
                    <p className="text-gray-500">Manage lecturer applications and active users for your institution.</p>
                </div>

                <div className="flex bg-white rounded-xl shadow-sm p-1 border border-gray-200">
                    <button
                        onClick={() => setActiveTab('applications')}
                        className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === 'applications' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-500 hover:bg-gray-50'}`}
                    >
                        Applications
                    </button>
                    <button
                        onClick={() => setActiveTab('active')}
                        className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === 'active' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-500 hover:bg-gray-50'}`}
                    >
                        Active Lecturers
                    </button>
                </div>
            </div>

            {/* Sleek Minimal Notification */}
            {notification.show && (
                <div className={`fixed top-6 right-6 z-[100] px-5 py-3 rounded-2xl shadow-2xl flex items-center gap-3 animate-in slide-in-from-right duration-300 backdrop-blur-xl border ${notification.type === 'error'
                        ? 'bg-red-50/90 border-red-100 text-red-600'
                        : 'bg-emerald-50/90 border-emerald-100 text-emerald-600'
                    }`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${notification.type === 'error' ? 'bg-red-100' : 'bg-emerald-100'
                        }`}>
                        {notification.type === 'error' ? <AlertCircle className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                    </div>
                    <span className="text-sm font-semibold tracking-tight">{notification.message}</span>
                </div>
            )}

            {activeTab === 'applications' ? (
                <div className="space-y-6">
                    {/* Stats Row */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {[
                            { label: 'Total Apps', value: stats.total, color: 'blue' },
                            { label: 'Pending Review', value: stats.pending, color: 'amber' },
                            { label: 'Interviews', value: stats.scheduled, color: 'indigo' },
                            { label: 'Approved', value: stats.approved, color: 'emerald' }
                        ].map((stat, i) => (
                            <div key={i} className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">{stat.label}</p>
                                <p className={`text-3xl font-semibold text-${stat.color}-600 tracking-tight`}>{stat.value}</p>
                            </div>
                        ))}
                    </div>

                    {/* Filters */}
                    <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row gap-4 items-center">
                        <div className="relative flex-1 w-full">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search by name or email..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500 outline-none"
                            />
                        </div>
                        <div className="flex items-center gap-2 w-full md:w-auto">
                            <Filter className="w-4 h-4 text-gray-400" />
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="flex-1 md:w-48 bg-gray-50 border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-bold text-gray-700 outline-none"
                            >
                                <option value="all">All Statuses</option>
                                <option value="pending">Pending</option>
                                <option value="interview_scheduled">Interview Scheduled</option>
                                <option value="approved">Approved</option>
                                <option value="rejected">Rejected</option>
                            </select>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="bg-gray-50/50 border-b border-gray-100">
                                    <tr>
                                        <th className="px-6 py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Applicant</th>
                                        <th className="px-6 py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Qualification</th>
                                        <th className="px-6 py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Experience</th>
                                        <th className="px-6 py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Submitted</th>
                                        <th className="px-6 py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Status</th>
                                        <th className="px-6 py-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50">
                                    {loading ? (
                                        <tr>
                                            <td colSpan="6" className="px-6 py-20 text-center">
                                                <Loader2 className="w-10 h-10 text-blue-500 animate-spin mx-auto mb-3" />
                                                <p className="text-gray-400 font-bold">Loading applications...</p>
                                            </td>
                                        </tr>
                                    ) : filteredApps.length === 0 ? (
                                        <tr>
                                            <td colSpan="6" className="px-6 py-20 text-center text-gray-400 font-bold">
                                                No applications found matching your filters.
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredApps.map((app) => (
                                            <tr key={app.id} className="hover:bg-blue-50/30 transition-colors group">
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-black">
                                                            {app.fullName.charAt(0)}
                                                        </div>
                                                        <div>
                                                            <p className="text-sm font-bold text-gray-900">{app.fullName}</p>
                                                            <p className="text-xs text-gray-500">{app.email}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className="px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-[10px] font-black uppercase">
                                                        {app.qualification}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <p className="text-sm text-gray-600">{app.experienceYears} Years</p>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <p className="text-xs text-gray-500">{new Date(app.createdAt).toLocaleDateString()}</p>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase ${app.status === 'approved' ? 'bg-emerald-50 text-emerald-700' :
                                                            app.status === 'rejected' ? 'bg-red-50 text-red-700' :
                                                                app.status === 'interview_scheduled' ? 'bg-blue-50 text-blue-700' :
                                                                    'bg-amber-50 text-amber-700'
                                                        }`}>
                                                        {app.status.replace('_', ' ')}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <div className="flex justify-end gap-2">
                                                        <button
                                                            onClick={() => { setSelectedApp(app); setShowDetailPanel(true); }}
                                                            className="p-2 hover:bg-blue-100 text-blue-600 rounded-lg transition-all"
                                                            title="View Details"
                                                        >
                                                            <Eye className="w-4 h-4" />
                                                        </button>
                                                        {app.status === 'pending' && (
                                                            <button
                                                                onClick={() => handleScheduleInterview(app.id)}
                                                                className="p-2 hover:bg-indigo-100 text-indigo-600 rounded-lg transition-all"
                                                                title="Schedule Interview"
                                                            >
                                                                <Video className="w-4 h-4" />
                                                            </button>
                                                        )}
                                                        {['pending', 'interview_scheduled'].includes(app.status) && (
                                                            <>
                                                                <button
                                                                    onClick={() => handleApprove(app.id)}
                                                                    className="p-2 hover:bg-emerald-100 text-emerald-600 rounded-lg transition-all"
                                                                    title="Approve"
                                                                >
                                                                    <UserPlus className="w-4 h-4" />
                                                                </button>
                                                                <button
                                                                    onClick={() => handleReject(app.id)}
                                                                    className="p-2 hover:bg-red-100 text-red-600 rounded-lg transition-all"
                                                                    title="Reject"
                                                                >
                                                                    <X className="w-4 h-4" />
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                        <h2 className="text-xl font-bold text-gray-900">Active Lecturers</h2>
                        <button className="flex items-center gap-2 text-sm font-bold text-blue-600 hover:text-blue-700">
                            <Download className="w-4 h-4" /> Export List
                        </button>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead className="bg-gray-50 border-b border-gray-100">
                                <tr>
                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Lecturer</th>
                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Qualification</th>
                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest">Joined On</th>
                                    <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                {activeLecturers.map((lec) => (
                                    <tr key={lec.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center font-black">
                                                    {lec.name.charAt(0)}
                                                </div>
                                                <div>
                                                    <p className="text-sm font-bold text-gray-900">{lec.name}</p>
                                                    <p className="text-xs text-gray-500">{lec.email}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-600 text-[10px] font-black uppercase">
                                                {lec.qualification || 'N/A'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-xs text-gray-500">
                                            {new Date(lec.createdAt).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <button className="p-2 hover:bg-gray-100 text-gray-400 rounded-lg">
                                                <MoreHorizontal className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ── Detail Panel ── */}
            {showDetailPanel && selectedApp && (() => {
                const isApproved = selectedApp.status === 'approved';
                const isRejected = selectedApp.status === 'rejected';

                const docUrl = (rawUrl) => {
                    if (!rawUrl) return '#';
                    if (rawUrl.startsWith('http')) return rawUrl;
                    const clean = rawUrl.startsWith('/') ? rawUrl : `/${rawUrl}`;
                    // Avoid double /uploads
                    const path = clean.startsWith('/uploads') ? clean : `/uploads${clean}`;
                    return `https://api.intuitlms.online/api${path}`;
                };

                const DocRow = ({ docKey, label, url, icon: Icon, iconBg, iconColor }) => {
                    const ds = selectedApp.documentStatus?.[docKey];
                    const isVerified = ds?.status === 'approved';
                    const isDeclined = ds?.status === 'rejected';

                    return (
                        <div className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:border-gray-200 bg-white transition-all group">
                            <div className={`w-8 h-8 rounded-lg ${iconBg} flex items-center justify-center flex-shrink-0`}>
                                <Icon className={`w-4 h-4 ${iconColor}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-gray-700 truncate">{label}</p>
                                {ds && (
                                    <p className={`text-[10px] font-semibold uppercase tracking-wide ${isVerified ? 'text-emerald-500' : 'text-red-400'}`}>
                                        {ds.status}{ds.reason ? ` · ${ds.reason}` : ''}
                                    </p>
                                )}
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                                {/* Download */}
                                <a
                                    href={docUrl(url)}
                                    target="_blank"
                                    rel="noreferrer"
                                    title="View / Download"
                                    className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-all"
                                >
                                    <Download className="w-3.5 h-3.5" />
                                </a>
                                <div className="w-px h-4 bg-gray-100" />
                                {/* Approve */}
                                <button
                                    onClick={() => handleVerifyDocument(selectedApp.id, docKey, 'approved')}
                                    title="Approve document"
                                    className={`w-7 h-7 flex items-center justify-center rounded-lg transition-all ${isVerified
                                            ? 'bg-emerald-50 text-emerald-500 border border-emerald-100'
                                            : 'text-gray-300 hover:text-emerald-500 hover:bg-emerald-50'
                                        }`}
                                >
                                    <Check className="w-3.5 h-3.5" />
                                </button>
                                {/* Reject */}
                                <button
                                    onClick={() => handleVerifyDocument(selectedApp.id, docKey, 'rejected')}
                                    title="Reject document"
                                    className={`w-7 h-7 flex items-center justify-center rounded-lg transition-all ${isDeclined
                                            ? 'bg-red-50 text-red-400 border border-red-100'
                                            : 'text-gray-300 hover:text-red-400 hover:bg-red-50'
                                        }`}
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </div>
                    );
                };

                return (
                    <div className="fixed inset-0 z-50 flex justify-end">
                        <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px]" onClick={() => setShowDetailPanel(false)} />
                        <div className="relative w-full max-w-md bg-white shadow-2xl h-full flex flex-col animate-in slide-in-from-right duration-250">

                            {/* Header */}
                            <div className="flex items-start justify-between p-5 border-b border-gray-100">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center text-base font-semibold flex-shrink-0">
                                        {selectedApp.fullName.charAt(0)}
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h2 className="text-sm font-semibold text-gray-900">{selectedApp.fullName}</h2>
                                            <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${isApproved ? 'bg-emerald-50 text-emerald-600' :
                                                    isRejected ? 'bg-red-50 text-red-500' :
                                                        selectedApp.status === 'interview_scheduled' ? 'bg-blue-50 text-blue-600' :
                                                            'bg-amber-50 text-amber-600'
                                                }`}>
                                                {selectedApp.status.replace(/_/g, ' ')}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-3 mt-0.5">
                                            <span className="text-[11px] text-gray-400 flex items-center gap-1">
                                                <Mail className="w-3 h-3" />{selectedApp.email}
                                            </span>
                                            <span className="text-[11px] text-gray-400 flex items-center gap-1">
                                                <Phone className="w-3 h-3" />{selectedApp.phoneNumber}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <button onClick={() => setShowDetailPanel(false)} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 transition-all">
                                    <X className="w-4 h-4" />
                                </button>
                            </div>

                            {/* Scrollable body */}
                            <div className="flex-1 overflow-y-auto p-5 space-y-5">

                                {/* Qualifications */}
                                <div className="flex gap-4">
                                    <div className="flex items-center gap-1.5 text-gray-500">
                                        <GraduationCap className="w-3.5 h-3.5 text-gray-400" />
                                        <span className="text-xs font-medium">{selectedApp.qualification}</span>
                                    </div>
                                    <div className="flex items-center gap-1.5 text-gray-500">
                                        <Briefcase className="w-3.5 h-3.5 text-gray-400" />
                                        <span className="text-xs font-medium">{selectedApp.experienceYears} Yrs Exp.</span>
                                    </div>
                                </div>

                                {/* Approved banner */}
                                {isApproved && (
                                    <div className="flex items-center gap-2.5 p-3 bg-emerald-50 border border-emerald-100 rounded-xl">
                                        <div className="w-7 h-7 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                                            <UserCheck className="w-4 h-4 text-emerald-600" />
                                        </div>
                                        <div>
                                            <p className="text-xs font-semibold text-emerald-700">Lecturer Approved</p>
                                            <p className="text-[10px] text-emerald-500">Account has been created and credentials sent.</p>
                                        </div>
                                    </div>
                                )}

                                {/* Bio */}
                                {selectedApp.bio && (
                                    <div>
                                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1.5">Bio</p>
                                        <p className="text-xs text-gray-500 leading-relaxed italic">{selectedApp.bio}</p>
                                    </div>
                                )}

                                {/* Documents */}
                                <div>
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Documents</p>
                                    <div className="space-y-2">
                                        {selectedApp.idDocumentUrl && (
                                            <DocRow
                                                docKey="idDocument"
                                                label="Identity Document"
                                                url={selectedApp.idDocumentUrl}
                                                icon={FileText}
                                                iconBg="bg-blue-50"
                                                iconColor="text-blue-500"
                                            />
                                        )}
                                        {selectedApp.certificateUrls && selectedApp.certificateUrls.map((url, i) => (
                                            <DocRow
                                                key={i}
                                                docKey={`cert-${i}`}
                                                label={`Certificate ${i + 1}`}
                                                url={url}
                                                icon={Award}
                                                iconBg="bg-indigo-50"
                                                iconColor="text-indigo-500"
                                            />
                                        ))}
                                        {!selectedApp.idDocumentUrl && (!selectedApp.certificateUrls || selectedApp.certificateUrls.length === 0) && (
                                            <p className="text-xs text-gray-400 italic">No documents submitted.</p>
                                        )}
                                    </div>
                                </div>

                                {/* Interview Control */}
                                <div>
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Interview</p>
                                    <div className="p-4 bg-blue-600 rounded-2xl text-white">
                                        <div className="flex items-center gap-2 mb-3">
                                            <Video className="w-4 h-4 text-blue-200" />
                                            <span className="text-xs font-semibold">Virtual Interview Control</span>
                                        </div>
                                        {!selectedApp.interviewRoomName ? (
                                            <div>
                                                <p className="text-[11px] text-blue-200 mb-3">Generate a LiveKit room to schedule the interview.</p>
                                                <button
                                                    onClick={() => handleScheduleInterview(selectedApp.id)}
                                                    disabled={isActionLoading}
                                                    className="w-full bg-white text-blue-600 text-xs font-semibold py-2.5 rounded-xl hover:bg-blue-50 transition-all"
                                                >
                                                    {isActionLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mx-auto" /> : 'Generate Interview Room'}
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="space-y-3">
                                                <div className="flex items-center justify-between p-2.5 bg-white/10 rounded-xl border border-white/10">
                                                    <div className="flex items-center gap-2">
                                                        <div className={`w-1.5 h-1.5 rounded-full ${selectedApp.interviewIsActive ? 'bg-emerald-400 animate-pulse' : 'bg-white/30'}`} />
                                                        <span className="text-[11px] font-semibold tracking-wide">
                                                            {selectedApp.interviewIsActive ? 'Live' : 'Room Ready'}
                                                        </span>
                                                    </div>
                                                    <span className="text-[9px] opacity-50 uppercase tracking-widest truncate max-w-[120px]">
                                                        {selectedApp.interviewRoomName}
                                                    </span>
                                                </div>
                                                <div className="grid grid-cols-2 gap-2">
                                                    <button
                                                        onClick={() => handleToggleInterviewActive(selectedApp.id, selectedApp.interviewIsActive)}
                                                        disabled={isActionLoading}
                                                        className={`py-2 rounded-xl text-xs font-semibold transition-all ${selectedApp.interviewIsActive
                                                                ? 'bg-amber-400 text-amber-900'
                                                                : 'bg-emerald-400 text-emerald-900'
                                                            }`}
                                                    >
                                                        {selectedApp.interviewIsActive ? 'End Session' : 'Start Session'}
                                                    </button>
                                                    <button className="py-2 rounded-xl text-xs font-semibold bg-white/20 hover:bg-white/30 text-white transition-all">
                                                        Join as Panel
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Footer actions — hidden for approved / rejected */}
                            {['pending', 'interview_scheduled'].includes(selectedApp.status) && (
                                <div className="p-4 border-t border-gray-100 flex gap-2">
                                    <button
                                        onClick={() => handleApprove(selectedApp.id)}
                                        disabled={isActionLoading}
                                        className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold py-2.5 rounded-xl flex items-center justify-center gap-1.5 transition-all shadow-sm shadow-emerald-600/20"
                                    >
                                        {isActionLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><UserCheck className="w-3.5 h-3.5" /> Approve Candidate</>}
                                    </button>
                                    <button
                                        onClick={() => handleReject(selectedApp.id)}
                                        disabled={isActionLoading}
                                        className="px-4 text-xs font-semibold text-red-500 hover:bg-red-50 rounded-xl transition-all border border-red-100"
                                    >
                                        Reject
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                );
            })()}




            {/* Credentials Modal */}
            {showCredentialsModal && credentials && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-md"></div>
                    <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl p-8 animate-in zoom-in duration-300">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <UserCheck className="w-8 h-8 text-emerald-600" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900">Lecturer Activated!</h2>
                            <p className="text-gray-500 text-sm mt-2">Temporary credentials have been generated. Please share these with the lecturer securely.</p>
                        </div>

                        <div className="space-y-4 mb-8">
                            <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100">
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Username (Email)</p>
                                <p className="text-sm font-semibold text-gray-900">{credentials.email}</p>
                            </div>
                            <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100 flex justify-between items-center">
                                <div>
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Temporary Password</p>
                                    <p className="text-sm font-semibold text-blue-600 tracking-wider font-mono">{credentials.password}</p>
                                </div>
                                <button
                                    onClick={() => copyToClipboard(credentials.password)}
                                    className="p-2 hover:bg-blue-50 text-blue-600 rounded-lg transition-all"
                                >
                                    <Clipboard className="w-5 h-5" />
                                </button>
                            </div>
                        </div>

                        <button
                            onClick={() => setShowCredentialsModal(false)}
                            className="w-full bg-gray-900 hover:bg-gray-800 text-white font-semibold py-4 rounded-2xl flex items-center justify-center gap-3 transition-all"
                        >
                            Done <ArrowRight className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserManagement;
